import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from "firebase/firestore/lite";
import { db } from '../firebase';
import styled from 'styled-components';
import { motion, LazyMotion, domAnimation, m } from 'framer-motion';
import { useTranslation } from '../context/LanguageContext';
import { 
  ArrowRight, 
  CheckCircle, 
  CaretLeft, 
  CaretRight,
  User,
  Envelope,
  Users,
  ChatText,
  Phone,
  Buildings
} from "@phosphor-icons/react";

const PageContainer = styled.div`
  background: #0d1117;
  color: white;
  min-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  padding-bottom: 120px;
`;

const HeroSection = styled.section`
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: calc(10vh);
  padding-left: 2rem;
  padding-right: 2rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at center, rgba(58, 183, 255, 0.05) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  width: 100%;
  text-align: center;
  position: relative;
  z-index: 1;
  margin-bottom: 2rem;

  h1 {
    font-size: clamp(2.5rem, 5vw, 4rem);
    font-weight: 800;
    line-height: 1.1;
    margin-bottom: 1rem;
    background: linear-gradient(90deg, #fff, #b3b3b3);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    letter-spacing: -0.02em;
  }

  p {
    font-size: clamp(1rem, 2vw, 1.25rem);
    color: #8b949e;
    margin-bottom: 1.5rem;
    line-height: 1.6;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    letter-spacing: -0.01em;
  }
`;

const FormContainer = styled(m.div)`
  max-width: 600px;
  width: 100%;
  margin: -1rem auto 0;
  background: rgba(28, 28, 28, 0.5);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 2.5rem;
  box-shadow: 0 0 80px rgba(58, 183, 255, 0.15);

  @media (max-width: 640px) {
    padding: 1.5rem;
    margin: -0.5rem 1rem 0;
  }
`;

const ProgressBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 3rem;
  padding: 0 1rem;
  position: relative;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 2.5rem;
    right: 2.5rem;
    height: 2px;
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-50%);
  }
`;

const ProgressStep = styled.div<{ active: boolean; completed: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.completed ? '#3AB7FF' : props.active ? 'rgba(58, 183, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
  border: 2px solid ${props => props.completed || props.active ? '#3AB7FF' : 'rgba(255, 255, 255, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;
  flex-shrink: 0;

  svg {
    color: ${props => props.completed ? 'white' : '#8b949e'};
    width: 20px;
    height: 20px;
  }
`;

const FormField = styled(m.div)`
  margin-bottom: 2rem;
  position: relative;
  width: 100%;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;

  label {
    display: block;
    font-size: 1.5rem;
    color: white;
    margin-bottom: 1.5rem;
    text-align: center;
    line-height: 1.3;
  }

  input, select, textarea {
    width: 100%;
    padding: 1.2rem;
    background: rgba(255, 255, 255, 0.03);
    border: 2px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    color: white;
    font-size: 1.1rem;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: #3AB7FF;
      box-shadow: 0 0 0 4px rgba(58, 183, 255, 0.1);
      background: rgba(255, 255, 255, 0.05);
    }

    &::placeholder {
      color: #8b949e;
    }

    &:hover:not(:focus) {
      border-color: rgba(255, 255, 255, 0.2);
      background: rgba(255, 255, 255, 0.04);
    }
  }

  textarea {
    min-height: 150px;
    resize: vertical;
    max-height: 300px;
  }

  select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%238b949e' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 1rem center;
    background-size: 1rem;
    padding-right: 2.5rem;
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  padding: 0 1rem;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const NavButton = styled(m.button)<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 1rem;
  background: ${props => props.variant === 'primary' ? '#3AB7FF' : 'transparent'};
  color: white;
  border: ${props => props.variant === 'primary' ? 'none' : '2px solid rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s;
  min-width: 120px;

  &:hover {
    background: ${props => props.variant === 'primary' ? '#50c1ff' : 'rgba(255, 255, 255, 0.05)'};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const SuccessMessage = styled(m.div)`
  text-align: center;
  color: #10B981;
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }

  p {
    color: #8b949e;
  }
`;

const ErrorMessage = styled(m.div)`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  color: #EF4444;
  margin-bottom: 1rem;
`;

interface FormData {
  name: string;
  email: string;
  phone: string;
  school: string;
  role: string;
  message: string;
}

const BetaSignupPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    school: '',
    role: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { t } = useTranslation();

  const formFields = [
    {
      id: 'name',
      label: 'What is your name?',
      type: 'text',
      placeholder: 'Enter your full name',
      icon: <User />,
      validation: (value: string) => value.length >= 2
    },
    {
      id: 'email',
      label: 'What is your email address?',
      type: 'email',
      placeholder: 'your.email@example.com',
      icon: <Envelope />,
      validation: (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    },
    {
      id: 'phone',
      label: 'What is your phone number?',
      type: 'tel',
      placeholder: 'Enter your phone number',
      icon: <Phone />,
      validation: (value: string) => value.length >= 10
    },
    {
      id: 'school',
      label: 'What is your school name?',
      type: 'text',
      placeholder: 'Enter your school name',
      icon: <Buildings />,
      validation: (value: string) => value.length >= 2
    },
    {
      id: 'role',
      label: 'What best describes your role?',
      type: 'select',
      icon: <Users />,
      options: [
        { value: '', label: 'Select your role' },
        { value: 'student', label: 'Student' },
        { value: 'parent', label: 'Parent' },
        { value: 'teacher', label: 'Teacher' },
        { value: 'school_admin', label: 'School Administrator' },
        { value: 'other', label: 'Other' }
      ],
      validation: (value: string) => value !== ''
    },
    {
      id: 'message',
      label: 'Why are you interested in our beta program?',
      type: 'textarea',
      placeholder: 'Tell us about your interest...',
      icon: <ChatText />,
      validation: (value: string) => value.length >= 10
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Auto-advance if field is valid
    const currentField = formFields[currentStep];
    if (currentField.validation(value) && currentStep < formFields.length - 1) {
      setTimeout(() => setCurrentStep(prev => prev + 1), 300);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      await addDoc(collection(db, "contactForms"), {
        ...formData,
        source: "BetaSignup",
        createdAt: serverTimestamp()
      });
      setFormStatus('success');
    } catch (error) {
      console.error("Error submitting form:", error);
      setFormStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred');
    }
  };

  const isFieldValid = (step: number) => {
    const field = formFields[step];
    return field.validation(formData[field.id as keyof FormData]);
  };

  const canProceed = isFieldValid(currentStep);

  return (
    <LazyMotion features={domAnimation}>
      <PageContainer>
        <HeroSection>
          <HeroContent>
            <m.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Be part of the future of education.
            </m.h1>
            <m.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Try Spark early. Tell us what works. Help us make it better.
            </m.p>
          </HeroContent>

          <FormContainer>
            {formStatus === 'success' ? (
              <SuccessMessage>
                <CheckCircle weight="bold" />
                <h3>Thank you for signing up!</h3>
                <p>We'll be in touch soon with more details about our beta program.</p>
              </SuccessMessage>
            ) : (
              <form onSubmit={handleSubmit}>
                <ProgressBar>
                  {formFields.map((field, index) => (
                    <ProgressStep 
                      key={field.id}
                      active={currentStep === index}
                      completed={index < currentStep || isFieldValid(index)}
                    >
                      {field.icon}
                    </ProgressStep>
                  ))}
                </ProgressBar>

                {currentStep >= 0 && (
                  <FormField
                    key={currentStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <label>{formFields[currentStep].label}</label>
                    {formFields[currentStep].type === 'select' ? (
                      <select
                        name={formFields[currentStep].id}
                        value={formData[formFields[currentStep].id as keyof FormData]}
                        onChange={handleInputChange}
                        required
                      >
                        {formFields[currentStep].options?.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : formFields[currentStep].type === 'textarea' ? (
                      <textarea
                        name={formFields[currentStep].id}
                        value={formData[formFields[currentStep].id as keyof FormData]}
                        onChange={handleInputChange}
                        placeholder={formFields[currentStep].placeholder}
                        required
                      />
                    ) : (
                      <input
                        type={formFields[currentStep].type}
                        name={formFields[currentStep].id}
                        value={formData[formFields[currentStep].id as keyof FormData]}
                        onChange={handleInputChange}
                        placeholder={formFields[currentStep].placeholder}
                        required
                      />
                    )}
                  </FormField>
                )}

                {formStatus === 'error' && (
                  <ErrorMessage>
                    <p>{errorMessage}</p>
                  </ErrorMessage>
                )}

                <NavigationButtons>
                  <NavButton
                    type="button"
                    onClick={() => setCurrentStep(prev => prev - 1)}
                    disabled={currentStep === 0}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <CaretLeft /> Back
                  </NavButton>
                  
                  {currentStep === formFields.length - 1 ? (
                    <NavButton
                      type="submit"
                      variant="primary"
                      disabled={!canProceed || formStatus === 'submitting'}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {formStatus === 'submitting' ? (
                        'Submitting...'
                      ) : (
                        <>
                          Complete <ArrowRight weight="bold" />
                        </>
                      )}
                    </NavButton>
                  ) : (
                    <NavButton
                      type="button"
                      variant="primary"
                      onClick={() => setCurrentStep(prev => prev + 1)}
                      disabled={!canProceed}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Next <CaretRight />
                    </NavButton>
                  )}
                </NavigationButtons>
              </form>
            )}
          </FormContainer>
        </HeroSection>
      </PageContainer>
    </LazyMotion>
  );
};

export default BetaSignupPage; 